import type { CoachingCall, CoachingCallCreateInput, CoachingCallUpdateInput } from '@doneverse/shared'
import { api } from '../api/index.js'

export interface ListCoachingCallsFilters {
  /** ISO datetime; only calls starting on/after this time are returned. */
  from?: string
  /** ISO datetime; only calls starting strictly before this time are returned. */
  to?: string
  limit?: number
}

export interface ListCoachingCallsResponse {
  items: CoachingCall[]
}

function buildQuery(params: Record<string, string | number | undefined>): string {
  const search = new URLSearchParams()
  for (const [key, value] of Object.entries(params)) {
    if (value === undefined) continue
    const str = String(value)
    if (str.length === 0) continue
    search.set(key, str)
  }
  const str = search.toString()
  return str ? `?${str}` : ''
}

export const coachingCallsService = {
  list: async (filters: ListCoachingCallsFilters = {}): Promise<ListCoachingCallsResponse> => {
    const qs = buildQuery({
      from: filters.from,
      to: filters.to,
      limit: filters.limit,
    })
    return api.get(`/coaching-calls${qs}`).json<ListCoachingCallsResponse>()
  },

  get: async (id: string): Promise<CoachingCall> => {
    return api.get(`/coaching-calls/${encodeURIComponent(id)}`).json<CoachingCall>()
  },

  // ─── Admin endpoints (require role=admin) ─────────────────────────────
  adminList: async (): Promise<ListCoachingCallsResponse> => {
    return api.get('/v1/admin/coaching-calls').json<ListCoachingCallsResponse>()
  },

  adminGet: async (id: string): Promise<CoachingCall> => {
    return api.get(`/v1/admin/coaching-calls/${encodeURIComponent(id)}`).json<CoachingCall>()
  },

  adminCreate: async (input: CoachingCallCreateInput): Promise<CoachingCall> => {
    return api.post(input, '/v1/admin/coaching-calls').json<CoachingCall>()
  },

  adminUpdate: async (id: string, patch: CoachingCallUpdateInput): Promise<CoachingCall> => {
    return api.patch(patch, `/v1/admin/coaching-calls/${encodeURIComponent(id)}`).json<CoachingCall>()
  },

  /**
   * `scope: 'following'` ends a recurring series at its next upcoming
   * occurrence rather than removing the row, so the calls that already happened
   * stay on the calendar. Omitted deletes the whole series.
   */
  adminDelete: async (id: string, scope?: 'following'): Promise<void> => {
    const qs = scope ? `?scope=${scope}` : ''
    await api.delete(`/v1/admin/coaching-calls/${encodeURIComponent(id)}${qs}`).res()
  },
}
